/* Sentinels and the layer manager.
 *
 * Z-ordering uses INVISIBLE SENTINEL LAYERS: one `background` layer per band,
 * `visibility: none`, added once in band order. To put a layer in a band you
 * pass the NEXT band's sentinel as MapLibre's `beforeId`, and it lands in the
 * right slice no matter what else is present or what order things were added
 * in. No magic numbers, and nothing has to be renumbered when a layer is added.
 *
 * The manager keeps AVAILABILITY separate from DESIRED VISIBILITY. A provider
 * going offline must not clobber the user's choice: the row greys out, and when
 * the provider comes back the layer returns to whatever they had set. Merging
 * the two is the bug that makes a chart quietly stop showing something you
 * turned on last week.
 */

import { untrack } from 'svelte';

import type { Map as MlMap } from 'maplibre-gl';

import {
  ensureLayerState,
  LAYER_STATE_KEY,
  migrateLayerState,
  serializeLayerState,
  type LayerState
} from './layerstate.ts';
import { bboxIntersects, type Bbox } from '../lib/geo.ts';
import { sources, type SourceName } from '../sources.ts';
import { Z_ORDER, type LayerContext, type LayerModule, type ZBand } from './types.ts';

export const sentinelId = (band: ZBand) => `__z__${band}`;

export const SOURCE_UNAVAILABLE_REASON: Record<SourceName, string> = {
  signalk: "Not on this chart — it needs the boat's instruments connected",
  'marine-data': "Not on this chart — it needs the boat's data feed",
  feeds: 'Not on this chart — no data feed on this server',
  'chart-cache': 'Not on this chart — no chart cache on this server',
  'static-data': 'Not on this chart — its data is missing'
};

/** The first source a layer declared and this host does not provide, or null.
 *  Pure, so `#availabilitySignature` can fold it in without side effects. */
function missingSource(m: LayerModule): SourceName | null {
  if (!m.requires) return null;
  const s = sources();
  for (const name of m.requires) if (!s.has(name)) return name;
  return null;
}

/** The view the manager last heard about. Null before the map's first move. */
let view: Bbox | null = null;

/**
 * Ids whose coverage does not reach the current view.
 *
 * ═══ COMPUTED ONCE PER MOVE, NOT ONCE PER QUESTION ═══
 *
 * `isVisible` is one of the hottest functions in the app: the layers panel, the
 * toggle bar, the legend, the 1 Hz sweep and the chart page's pick-candidate
 * `$derived` all call it, the last of those on every pointer move. Evaluating
 * `coverage()` inside it meant running `bboxOf` across a hundred and fifty tide
 * stations, then two hundred current stations, several times a frame.
 *
 * That was enough to starve the 1 Hz sweep — and a starved sweep stops fetching
 * fishery notices, stops refreshing rules and stops updating every live layer,
 * which _fishing-proof.mjs reported as "0 features drawn". The answer only
 * changes when the chart moves, so it is worked out when the chart moves.
 */
let outside = new Set<string>();

/** Recompute `outside` for every registered module. Untracked: a coverage
 *  function reads the store it derives from, and those are `$state` — tracked,
 *  they would make every caller a subscriber to every station list. */
function recomputeCoverage(modules: Iterable<LayerModule>): void {
  const here = view;
  outside = untrack(() => {
    const out = new Set<string>();
    if (here === null) return out;
    for (const m of modules) {
      if (!m.coverage) continue;
      const box = typeof m.coverage === 'function' ? m.coverage() : m.coverage;
      if (box != null && !bboxIntersects(box, here)) out.add(m.id);
    }
    return out;
  });
}

/**
 * Is this layer's coverage away from where we are looking?
 *
 * A plain set lookup. Absent from the set means "no objection", which covers
 * all three of: the layer claims everywhere, the view is not known yet, and the
 * layer's own data has not loaded so it cannot say where it applies. Greying a
 * layer out on a question nobody can answer yet is worse than showing it.
 */
function outOfCoverage(m: LayerModule, chartUncovered: Set<string>): boolean {
  return outside.has(m.id) || chartUncovered.has(m.id);
}

export function installSentinels(map: MlMap): void {
  for (const band of Z_ORDER) {
    const id = sentinelId(band);
    if (!map.getLayer(id)) {
      map.addLayer({ id, type: 'background', layout: { visibility: 'none' }, paint: {} });
    }
  }
}

export function beforeIdFor(map: MlMap, band: ZBand): string | undefined {
  const i = Z_ORDER.indexOf(band);
  for (let j = i + 1; j < Z_ORDER.length; j++) {
    const id = sentinelId(Z_ORDER[j]!);
    if (map.getLayer(id)) return id;
  }
  return undefined;
}

function loadState(): Record<string, LayerState> {
  try {
    /* migrateLayerState owns the versioning: a legacy (pre-fix) blob on this
     * key gets the audit migration (zones/currents/VESSEL-ECHO forced ON), and
     * a versioned blob is the user's own state, taken as-is. See layerstate.ts. */
    return migrateLayerState(localStorage.getItem(LAYER_STATE_KEY));
  } catch {
    return {};
  }
}

class LayerManager {
  #map: MlMap | null = null;
  #modules = new Map<string, LayerModule>();
  #added = new Set<string>();
  #chartUncovered = new Set<string>();

  /** What the user asked for. Survives a provider going away. */
  state = $state<Record<string, LayerState>>(loadState());
  /** Bumped when a module is registered or availability changes. */
  rev = $state(0);

  night = $state(false);

  get map(): MlMap | null {
    return this.#map;
  }

  #ctx(): LayerContext | null {
    const map = this.#map;
    if (!map) return null;
    return { map, beforeIdFor: (b) => beforeIdFor(map, b) };
  }

  attach(map: MlMap): void {
    this.#map = map;
    installSentinels(map);
    for (const m of this.#modules.values()) void this.#sync(m);
  }

  /** After a base-style swap every layer is gone; put them all back. */
  async reattach(): Promise<void> {
    const ctx = this.#ctx();
    if (!ctx) return;
    installSentinels(ctx.map);
    this.#added.clear();
    for (const m of this.#modules.values()) {
      if (m.reattach) await m.reattach(ctx);
      await this.#sync(m);
    }
  }

  register(...modules: LayerModule[]): void {
    for (const m of modules) {
      this.#modules.set(m.id, m);
      /* ensureLayerState: a layer with no persisted entry takes its module's
       * default — which is how a NEW layer's default of ON shows without a
       * migration edit. Kept in layerstate.ts so the rule is unit-testable. */
      this.state = ensureLayerState(this.state, m.id, m);
      void this.#sync(m);
    }
    this.rev++;
    recomputeCoverage(this.#modules.values());
    /* Re-baseline, so the first `pollAvailability` tick after a registration
     * compares against what these modules actually answer rather than against
     * an empty string — otherwise the first real change is the one that gets
     * swallowed as "that's just the baseline". */
    this.#availSig = this.#availabilitySignature();
  }

  list(): LayerModule[] {
    void this.rev;
    return [...this.#modules.values()];
  }

  get(id: string): LayerModule | undefined {
    return this.#modules.get(id);
  }

  /* ═══ WHY THE FIRST LINE IS A REVISION READ ═══
   *
   * Reported from the boat: the toggle bar at the bottom of the expanded map
   * switched layers on and off, and no button ever lit up.
   *
   * `#modules` is a plain Map — deliberately, it holds closures over the map,
   * not data — so the ONLY reactive read below is `this.state[id]`, on the
   * last line. Every early return above it reads nothing reactive at all.
   *
   * ChartTools is a lazy chunk and mounts BEFORE ChartPage registers the
   * modules, so `class:on={layers.isVisible(id)}` first evaluates against an
   * empty `#modules`, takes the `if (!m)` return, and the effect ends up with
   * ZERO dependencies. Svelte has nothing to invalidate it on, so it never
   * runs again. Clicking still worked — `onclick` re-reads fresh, by which
   * time the module exists — which is exactly why the layer moved and the
   * button did not.
   *
   * `rev` bumps on register, on a visibility change, and on an availability
   * refresh, so reading it here gives every path a dependency and the panel,
   * the toggle bar and the legend all track the same three events. The panel
   * escaped the bug only because `list()` happens to read `rev` already. */
  isVisible(id: string): boolean {
    void this.rev;
    const m = this.#modules.get(id);
    if (!m) return false;
    if (m.pinned) return true;
    if (missingSource(m) !== null) return false;
    if (outOfCoverage(m, this.#chartUncovered)) return false;
    if (m.available && !m.available()) return false;
    if (m.parent && !this.isVisible(m.parent)) return false;
    return this.state[id]?.visible ?? false;
  }

  /**
   * Why this layer cannot be shown, in words, or null when it can.
   *
   * The panel asks this rather than assembling the reason itself, so a missing
   * provider reads the same as a provider that went away — which from the
   * cockpit it is. Note the ORDER: a source this host simply does not have is
   * reported before a module's own `available()`, because a layer with nowhere
   * to fetch from cannot meaningfully answer that question anyway.
   */
  unavailableReason(id: string): string | null {
    void this.rev;
    const m = this.#modules.get(id);
    if (!m) return null;
    const missing = missingSource(m);
    if (missing !== null) return SOURCE_UNAVAILABLE_REASON[missing];
    if (outOfCoverage(m, this.#chartUncovered)) return m.outOfCoverageHint ?? 'no data for this area';
    if (m.available && !m.available()) return m.unavailableHint ?? 'unavailable';
    return null;
  }

  async setVisible(id: string, visible: boolean): Promise<void> {
    const m = this.#modules.get(id);
    if (!m || m.pinned) return;

    // Exclusive groups: turning one on turns its siblings off, or the two
    // fills composite into something that means nothing.
    if (visible && m.exclusive) {
      for (const other of this.#modules.values()) {
        if (other.id !== id && other.exclusive === m.exclusive) {
          this.state[other.id] = { ...this.state[other.id]!, visible: false };
          await this.#sync(other);
        }
      }
    }

    this.state[id] = { ...(this.state[id] ?? { opacity: m.defaultOpacity ?? 1 }), visible };
    this.#save();
    /* A parent going dark changes what `isVisible` answers for its children
     * without touching THEIR state entry, and an exclusive group changes its
     * siblings the same way. Bump so those reads re-run too — the state write
     * above only invalidates readers of this one id. */
    this.rev++;
    await this.#sync(m);
    // A parent going dark takes its children with it.
    for (const child of this.#modules.values()) {
      if (child.parent === id) await this.#sync(child);
    }
  }

  /** Remove and re-add modules through their normal registration path. */
  async retry(ids: readonly string[]): Promise<void> {
    const ctx = this.#ctx();
    if (!ctx) return;
    for (const id of ids) {
      const m = this.#modules.get(id);
      if (!m) continue;
      if (this.#added.has(id)) {
        m.remove?.(ctx);
        this.#added.delete(id);
      }
      await this.#sync(m);
    }
  }

  setOpacity(id: string, opacity: number): void {
    const m = this.#modules.get(id);
    const ctx = this.#ctx();
    if (!m || !ctx) return;
    this.state[id] = { ...(this.state[id] ?? { visible: false }), opacity };
    this.#save();
    m.setOpacity?.(ctx, opacity);
  }

  setNight(night: boolean): void {
    this.night = night;
    const ctx = this.#ctx();
    if (!ctx) return;
    for (const m of this.#modules.values()) m.applyTheme?.(ctx, { night });
  }

  /**
   * Where the chart is looking. Drives `coverage`.
   *
   * ═══ RECORDS. DOES NOT NOTIFY. ═══
   *
   * This is called from the map's `moveend`, which follow mode fires
   * continuously while the boat moves. Three ways of announcing a coverage
   * change from here were tried and all three broke the chart:
   *
   *   folding it into the availability signature ran `refreshAvailability`,
   *   which `#sync`s every module — adding and removing MapLibre layers during
   *   a pan;
   *
   *   bumping `rev` put a write into a graph the 1 Hz sweep and the chart
   *   page's pick-`$derived` both read, and Svelte threw
   *   effect_update_depth_exceeded;
   *
   *   a dedicated `viewRev` read only by `unavailableReason` did the same, because
   *   `publishBounds` writes it on every frame of follow-mode movement.
   *
   * The failure is not subtle when it happens: the sweep dies, and with it the
   * fishery notices, the rules refresh and every live layer's update. It shows
   * up as _fishing-proof.mjs reporting "0 features drawn".
   *
   * So this records the view and tells nobody. `isVisible` and
   * `unavailableReason` compute coverage FRESH on every call, so any render
   * gets the right answer — the panel is opened, read and closed, and it
   * renders on open. The cost is that a panel left open while you pan across a
   * region boundary updates on the next `rev` bump rather than instantly. That
   * is a fair trade against a chart that stops updating.
   */
  setView(next: Bbox | null): void {
    view = next;
    recomputeCoverage(this.#modules.values());
  }

  /** Record the tile-derived coverage verdict without detaching map layers.
   * Called only from the tile-status/moveend coverage path, never the 1 Hz
   * availability sweep.
   *
   * The bump is conditional: `chartCoverage` calls this on every tile-status
   * pass and every `moveend`, and the chart's layers.rev effect re-runs
   * `chartCoverage` — an unconditional bump re-triggers that effect, which
   * re-runs coverage, which bumps again: a genuine infinite reactive loop
   * (Svelte's `effect_update_depth_exceeded`) that kills every control on the
   * page, because the batch is unlinked when the guard trips. Only an actual
   * change may invalidate. */
  setChartUncovered(id: string, uncovered: boolean): void {
    if (this.#chartUncovered.has(id) === uncovered) return;
    if (uncovered) this.#chartUncovered.add(id);
    else this.#chartUncovered.delete(id);
    this.rev++;
  }

  /** Availability changed under us — re-evaluate without touching intent. */
  refreshAvailability(): void {
    this.rev++;
    for (const m of this.#modules.values()) void this.#sync(m);
  }

  /** The last `available()` answers, joined — see `pollAvailability`. */
  #availSig = '';

  #availabilitySignature(): string {
    let sig = '';
    for (const m of this.#modules.values()) {
      /* A source can be reconfigured at runtime (apps/web lets the viewer point
       * at a different marine-data host), so it belongs in the signature
       * alongside the live predicates rather than being treated as fixed at
       * registration. */
      if (m.requires) sig += `${m.id}!${missingSource(m) ?? ''};`;
      if (m.available) sig += `${m.id}:${m.available() ? 1 : 0};`;
    }
    return sig;
  }

  /* `available()` is a live predicate: the wind forecast probes GeoMet, the
   * weather zones follow the pi's forecast proxy. Nothing polled it, so
   * `refreshAvailability()` existed and was called from nowhere in the repo,
   * and a layer that came back after a wifi drop stayed greyed out until a
   * reload. Cheap enough for the 1 Hz sweep BECAUSE it compares first: the
   * re-sync only happens on the tick where an answer actually changed. */
  pollAvailability(): void {
    /* Also here, not only on `setView`: a coverage FUNCTION can start
     * answering once its data lands, and nothing moves the chart to announce
     * that. 1 Hz is the right cadence for a question that changes on a passage. */
    recomputeCoverage(this.#modules.values());
    const sig = this.#availabilitySignature();
    if (sig === this.#availSig) return;
    this.#availSig = sig;
    this.refreshAvailability();
  }


  /** Why a layer failed to add, so the panel can say. */
  errors = $state<Record<string, string>>({});

  async #sync(m: LayerModule): Promise<void> {
    const ctx = this.#ctx();
    if (!ctx) return;
    const want = this.isVisible(m.id);

    /* Callers fire this with `void`, so without the try/catch an exception in
     * add() is swallowed and the layer simply never appears — no error, no
     * clue. That cost real time once already. */
    try {
      if (want && !this.#added.has(m.id)) {
        await m.add(ctx);
        this.#added.add(m.id);
        const o = this.state[m.id]?.opacity;
        if (o !== undefined && m.setOpacity) m.setOpacity(ctx, o);
        m.applyTheme?.(ctx, { night: this.night });
      }
      if (this.#added.has(m.id)) m.setVisible(ctx, want);
      if (this.errors[m.id]) delete this.errors[m.id];
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.errors[m.id] = msg;
      console.error(`[layers] ${m.id} failed:`, e);
    }
  }

  #save(): void {
    try {
      /* Versioned payload, so a later migration can tell this build's state
       * from a legacy blob still sitting on the same key. */
      localStorage.setItem(LAYER_STATE_KEY, serializeLayerState(this.state));
    } catch {
      /* quota — layers still work, they just do not survive a reload */
    }
  }
}

export const layers = new LayerManager();
